import { ABILITY_CATALOG } from './abilities/abilityCatalog.js';
import { StatusEffectManager } from './statusEffectManager.js';
import { processKillReward } from './combat/killRewardProcessor.js';
import { resolveMitigatedDamage } from './combat/applyMitigatedDamage.js';
import { applyVampHealFromDamage } from './combat/vampHeal.js';
import { scheduleAramChampionRespawn } from './map/aramChampionRespawn.js';
import { EntityStats } from './entity/entityStats.js';
import {
  collidesWith as _collidesWith,
  collidesWithBox as _collidesWithBox,
  isTerrainPassable as _isTerrainPassable,
  getBoxContactNormal as _getBoxContactNormal,
  clampToBounds as _clampToBounds
} from './entity/entityCollision.js';
import { checkBushZones as _checkBushZones, canSeeEntity as _canSeeEntity } from './entity/entityVision.js';
import { defaultChampionVisionRadius } from './vision.js';
import { tickStunTimer, tickHawkVision, tickAbilityCooldowns, tickManaRegen } from './entity/entityAbilitySystem.js';
import { tickChargeRecharge } from './abilities/abilityCharges.js';
import {
  isInAttackRange as _isInAttackRange,
  issueAttackCommand as _issueAttackCommand,
  cancelAttackWithMovement as _cancelAttackWithMovement,
  updateAttackCycle as _updateAttackCycle
} from './entity/attackCycleStateMachine.js';
import { updatePosition as _updatePosition } from './entity/movementPhysics.js';

export class Entity {
  constructor(config = {}) {
    const { id, type = 'champion', team = 'blue', x = 0, y = 0, z = 0, radius = 20, name = '', abilities = null } = config;
    this.id = id;
    this.type = type;
    this.team = team;
    this.name = name || id;
    this.x = x;
    this.y = y;
    this.z = z;
    this.radius = radius;
    this.isDead = false;

    this.stats = new EntityStats(config);
    this.isStunned = false;
    this.stunTimer = 0;
    this.statusManager = new StatusEffectManager();
    this.buffs = [];
    this.shieldHp = 0;
    this.shieldTimer = 0;

    this.abilities = abilities || {
      Q: 'q_rangers_focus',
      W: 'w_volley',
      E: 'e_hawkshot',
      R: 'r_crystal_arrow'
    };

    this.qActive = false;
    this.qTimer = 0;
    this.qCooldown = 0;
    this.maxQCooldown = ABILITY_CATALOG.q_rangers_focus.cooldown;
    this.qDuration = ABILITY_CATALOG.q_rangers_focus.duration;

    this.wCooldown = 0;
    this.maxWCooldown = ABILITY_CATALOG.w_volley.cooldown;
    this.wDamageBase = ABILITY_CATALOG.w_volley.baseDamage;

    this.eCooldown = 0;
    this.maxECooldown = ABILITY_CATALOG.e_hawkshot.cooldown;
    this.revealedByHawk = false;
    this.hawkVisionTimer = 0;

    this.rCooldown = 0;
    this.maxRCooldown = ABILITY_CATALOG.r_crystal_arrow.cooldown;

    this.windupPercent = 0.219;
    this.windupTimer = 0;
    this.attackCooldownTimer = 0;
    this.attackCooldown = 0;
    this.targetX = x;
    this.targetZ = z;
    this.facingAngle = 0;
    this.isMoving = false;
    this.hasActiveCollision = false;
    this.isSliding = false;
    this.inBush = false;
    this.visionRadius = config.visionRadius ?? defaultChampionVisionRadius();
    this.selectedTarget = null;
    this.attackTarget = null;
    this.isAttacking = false;
    this.hasFiredCurrentAttack = false;
    this.lastCancelEvent = null;
    this.cancelEventTimer = 0;
  }

  get hp() { return this.stats.hp; }
  set hp(val) { this.stats.hp = val; }
  get maxHp() { return this.stats.maxHp; }
  set maxHp(val) { this.stats.maxHp = val; }
  get ad() { return this.stats.ad; }
  set ad(val) { this.stats.ad = val; }
  get ap() { return this.stats.ap; }
  set ap(val) { this.stats.ap = val; }
  get ms() { return this.stats.ms; }
  set ms(val) { this.stats.ms = val; }
  get as() { return this.stats.as; }
  set as(val) { this.stats.as = val; }
  get attackRange() { return this.stats.attackRange; }
  set attackRange(val) { this.stats.attackRange = val; }
  get armor() { return this.stats.armor; }
  set armor(val) { this.stats.armor = val; }
  get mr() { return this.stats.mr; }
  set mr(val) { this.stats.mr = val; }
  get critChance() { return this.stats.critChance; }
  set critChance(val) { this.stats.critChance = val; }
  get gold() { return this.stats.gold; }
  set gold(val) { this.stats.gold = val; }
  get exp() { return this.stats.exp; }
  set exp(val) { this.stats.exp = val; }
  get xp() { return this.stats.exp; }
  set xp(val) { this.stats.exp = val; }
  get level() { return this.stats.level; }
  set level(val) { this.stats.level = val; }
  get bounty() { return this.stats.bounty; }
  set bounty(val) { this.stats.bounty = val; }
  get cs() { return this.stats.cs; }
  set cs(val) { this.stats.cs = val; }
  get mp() { return this.stats.mp; }
  set mp(val) { this.stats.mp = val; }
  get maxMp() { return this.stats.maxMp; }
  set maxMp(val) { this.stats.maxMp = val; }
  get mpRegen() { return this.stats.mpRegen; }
  set mpRegen(val) { this.stats.mpRegen = val; }

  addExperience(amount) { return this.stats.addExperience(amount, this.type, this.isDead); }
  getEffectiveStats() { return this.stats.getEffectiveStats(this.buffs); }

  canMove() {
    if (this.isDead || this.isStunned) return false;
    if (this.statusManager && (this.statusManager.hasEffect('stun') || this.statusManager.hasEffect('root') || this.statusManager.hasEffect('knockup'))) {
      return false;
    }
    return true;
  }

  canAttack() {
    if (this.isDead || this.isStunned) return false;
    if (this.statusManager && (this.statusManager.hasEffect('stun') || this.statusManager.hasEffect('knockup'))) {
      return false;
    }
    return true;
  }

  canCastSkills() {
    if (this.isDead || this.isStunned) return false;
    if (this.statusManager && (this.statusManager.hasEffect('stun') || this.statusManager.hasEffect('silence') || this.statusManager.hasEffect('knockup'))) {
      return false;
    }
    return true;
  }

  applyStun(duration) {
    if (this.isDead || duration <= 0) return;
    this.isStunned = true;
    this.stunTimer = Math.max(this.stunTimer, duration);
    this.isMoving = false;
    this.isAttacking = false;
    this.attackTarget = null;
  }

  equipAbility(slotKey, abilityId) {
    if (ABILITY_CATALOG[abilityId]) {
      this.abilities[slotKey.toUpperCase()] = abilityId;
      return true;
    }
    return false;
  }

  grantHawkVision(duration = 5.0) {
    this.revealedByHawk = true;
    this.hawkVisionTimer = Math.max(this.hawkVisionTimer, duration);
  }

  updateRegen(dt) { tickManaRegen(this, dt); }
  consumeMana(amount) { return this.stats.consumeMana(amount, this.isDead); }

  activateQ() {
    if (this.isDead || this.isStunned || this.qActive || this.qCooldown > 0) return false;
    if (!this.consumeMana(30)) return false;
    this.qActive = true;
    this.qTimer = this.qDuration;
    return true;
  }

  castW(targetAngle) {
    if (this.isDead || this.isStunned || this.wCooldown > 0) return null;
    if (!this.consumeMana(75)) return null;
    this.wCooldown = this.maxWCooldown;
    this.facingAngle = targetAngle;
    const spec = ABILITY_CATALOG.w_volley;
    const count = spec.projectilesCount;
    const cone = spec.coneAngleRad;
    const start = targetAngle - cone / 2;
    const step = count > 1 ? cone / (count - 1) : 0;
    const arrowAngles = [];
    for (let i = 0; i < count; i++) {
      arrowAngles.push(start + step * i);
    }
    return arrowAngles;
  }

  castE(targetX, targetZ) {
    if (this.isDead || this.isStunned || this.eCooldown > 0) return null;
    if (!this.consumeMana(0)) return null;
    this.eCooldown = this.maxECooldown;
    const dx = targetX - this.x;
    const dz = targetZ - this.z;
    this.facingAngle = Math.atan2(dx, dz);
    return { targetX, targetZ };
  }

  castR(targetAngle) {
    if (this.isDead || this.isStunned || this.rCooldown > 0) return null;
    if (!this.consumeMana(100)) return null;
    this.rCooldown = this.maxRCooldown;
    this.facingAngle = targetAngle;
    return targetAngle;
  }

  updateAbilities(dt) {
    this.updateRegen(dt);
    if (this.statusManager) this.statusManager.update(dt);
    tickStunTimer(this, dt);
    tickHawkVision(this, dt);
    tickAbilityCooldowns(this, dt);
    if (this.hawkshotCharges) tickChargeRecharge(this.hawkshotCharges, dt);
  }

  update(dt = 0.016) {
    if (this.isDead) return;
    this.updateAbilities(dt);
    this.updateAttackCycle(dt);
    if (this.attackCooldown > 0) {
      this.attackCooldown = Math.max(0, this.attackCooldown - dt);
    }
    this.updatePosition(dt);
  }

  cancelAttackWithMovement(newTargetX, newTargetZ) {
    _cancelAttackWithMovement(this, newTargetX, newTargetZ);
  }

  takeDamage(amount, sourceEntity = null, damageType = 'physical', isCrit = false) {
    if (this.isDead || amount <= 0) return { hp: this.hp, killed: false, isCrit: false };
    let effectiveDamage = Math.round(
      resolveMitigatedDamage(amount, damageType, this.armor, this.mr)
    );
    if (this.shieldHp > 0 && effectiveDamage > 0) {
      const absorbed = Math.min(this.shieldHp, effectiveDamage);
      this.shieldHp -= absorbed;
      effectiveDamage -= absorbed;
      if (this.shieldHp <= 0) {
        this.shieldHp = 0;
        this.shieldTimer = 0;
      }
    }
    if (effectiveDamage <= 0) {
      return { hp: this.hp, killed: false, isCrit, sourceEntity, effectiveDamage: 0 };
    }

    this.hp = Math.max(0, this.hp - effectiveDamage);
    if (sourceEntity) {
      applyVampHealFromDamage(sourceEntity, effectiveDamage, damageType);
    }
    let killed = false;
    if (typeof this.onDamageCallback === 'function') {
      this.onDamageCallback({ effectiveDamage, damageType, isCrit, sourceEntity });
    }
    if (this.hp <= 0) {
      this.isDead = true;
      this.isMoving = false;
      this.isAttacking = false;
      this.isStunned = false;
      this.stunTimer = 0;
      this.attackTarget = null;
      this.selectedTarget = null;
      killed = true;
      processKillReward(this, sourceEntity);
      if (this.type === 'champion') scheduleAramChampionRespawn(this);
    }
    return { hp: this.hp, killed, sourceEntity, effectiveDamage };
  }

  respawn(x = this.x, z = this.z) {
    this.hp = this.maxHp;
    this.isDead = false;
    this.isStunned = false;
    this.stunTimer = 0;
    this.x = x;
    this.z = z;
    this.targetX = x;
    this.targetZ = z;
    this.isMoving = false;
    this.isAttacking = false;
  }

  moveTo(x, y, z) {
    if (this.isDead || this.isStunned) return;
    this.targetX = x;
    this.targetY = y !== undefined ? y : this.y;
    this.targetZ = z !== undefined ? z : (y !== undefined ? y : this.z);
    this.isMoving = true;
  }

  selectTarget(targetEntity) {
    if (targetEntity && !targetEntity.isDead && targetEntity !== this && targetEntity.team !== this.team) {
      this.selectedTarget = targetEntity;
      return true;
    }
    this.selectedTarget = null;
    return false;
  }

  issueAttackCommand(enemyEntity) { return _issueAttackCommand(this, enemyEntity); }
  isInAttackRange(targetEntity) { return _isInAttackRange(this, targetEntity); }
  updateAttackCycle(dt) { return _updateAttackCycle(this, dt); }
  collidesWith(otherEntity, margin = 0.25) { return _collidesWith(this, otherEntity, margin); }
  collidesWithBox(box, margin = 0) { return _collidesWithBox(this, box, margin); }
  isTerrainPassable(x, z, impassableZones = []) { return _isTerrainPassable(this, x, z, impassableZones); }
  checkBushZones(bushZones = []) {
    this.inBush = _checkBushZones(this, bushZones);
    return this.inBush;
  }
  canSeeEntity(targetEntity, bushZones = [], activeWards = [], wallBoxes = []) {
    return _canSeeEntity(this, targetEntity, bushZones, activeWards, wallBoxes);
  }
  getBoxContactNormal(box) { return _getBoxContactNormal(this, box); }
  clampToBounds(x, z, bounds = { minX: -14, maxX: 14, minZ: -14, maxZ: 14 }) { return _clampToBounds(x, z, bounds); }
  updatePosition(dt, obstacleEntities, mapBounds, wallBoxes, bushZones, impassableZones) { return _updatePosition(this, dt, obstacleEntities, mapBounds, wallBoxes, bushZones, impassableZones); }
}
